import type {
  SpotAdmissionRound,
  SpotCollege,
  SpotRoundData,
  SpotSeatMatrix,
  SpotSeatValue,
} from "./spotRoundTypes";

const STORAGE_KEY = "ht_spotrounds_v8";
const SHORTLIST_KEY = "ht_spotrounds_shortlist_v1";

export const STREAMS = [
  "Civil",
  "Computer Science",
  "CS (AI & ML)",
  "CS (Data Science)",
  "E&TC",
  "Electrical",
  "Information Technology",
  "Mechanical",
] as const;

export const CAT_JEE = ["All India (Open)"] as const;

export const CAT_MHTCET = [
  "Open",
  "SC",
  "ST",
  "VJ (NT-A)",
  "NT-B",
  "NT-C",
  "NT-D",
  "OBC",
  "SBC",
  "EWS",
  "PWD",
] as const;

const NAMES = [
  "Vishwakarma Institute of Technology",
  "Deccan College of Engineering",
  "Konkan Institute of Engineering",
  "Bhosale College of Engineering",
  "Nagpur Institute of Technology",
  "Godavari College of Engineering",
  "Sahyadri Institute of Technology",
  "Solapur Engineering College",
  "Thane Institute of Management & Engineering",
  "Kolhapur College of Engineering",
];

const CITIES = [
  "Pune",
  "Pune",
  "Ratnagiri",
  "Aurangabad",
  "Nagpur",
  "Nashik",
  "Satara",
  "Solapur",
  "Thane",
  "Kolhapur",
];

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function rnd(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function uid(prefix = "id"): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

function shortForm(name: string): string {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 5)
    .toUpperCase();
}

function seedMatrix(): SpotSeatMatrix {
  const matrix: SpotSeatMatrix = { jee: {}, mhtcet: {} };

  CAT_JEE.forEach((category) => {
    matrix.jee[category] = {};
    STREAMS.forEach((stream) => {
      const value = rnd(0, 18);
      matrix.jee[category][stream] = value === 0 && Math.random() > 0.6 ? "" : value;
    });
  });

  CAT_MHTCET.forEach((category) => {
    matrix.mhtcet[category] = {};
    STREAMS.forEach((stream) => {
      const value = rnd(0, 14);
      matrix.mhtcet[category][stream] = value === 0 && Math.random() > 0.5 ? "" : value;
    });
  });

  return matrix;
}

function blankMatrix(): SpotSeatMatrix {
  const matrix: SpotSeatMatrix = { jee: {}, mhtcet: {} };

  CAT_JEE.forEach((category) => {
    matrix.jee[category] = {};
    STREAMS.forEach((stream) => {
      matrix.jee[category][stream] = "";
    });
  });

  CAT_MHTCET.forEach((category) => {
    matrix.mhtcet[category] = {};
    STREAMS.forEach((stream) => {
      matrix.mhtcet[category][stream] = "";
    });
  });

  return matrix;
}

function seedRounds(): SpotAdmissionRound[] {
  const statuses: Array<SpotAdmissionRound["status"]> = ["Ended", "Active", "Not Started"];
  const windows: Array<[string, string]> = [
    ["2026-06-14", "2026-06-16"],
    ["2026-06-18", "2026-06-21"],
    ["2026-07-02", "2026-07-05"],
  ];
  const notes = [
    "Round Closed.",
    "Fill seats promptly — candidates must confirm within 48 hours of allotment.",
    "",
  ];

  return [1, 2, 3].map((n, index) => {
    const status = statuses[index];
    return {
      id: uid("rnd"),
      name: `Round ${n}`,
      status,
      dayStart: windows[index][0],
      dayEnd: windows[index][1],
      notes: notes[index],
      matrix: status === "Not Started" ? blankMatrix() : seedMatrix(),
    };
  });
}

function seedData(): SpotRoundData {
  const colleges: SpotCollege[] = NAMES.map((name, index) => {
    const sf = shortForm(name);
    return {
      id: uid("col"),
      name,
      shortName: sf,
      address: `${CITIES[index]}, Maharashtra, India`,
      logoInitial: sf.slice(0, 2),
      rounds: seedRounds(),
      courses: STREAMS.map((stream) => ({ name: stream, status: "Active" as const })),
      lastUpdated: Date.now() - rnd(5, 50) * 3600000,
    };
  });

  return { colleges };
}

export function loadSpotRoundData(): SpotRoundData {
  if (!canUseLocalStorage()) {
    return seedData();
  }

  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    const data = seedData();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return data;
  }

  try {
    const parsed = JSON.parse(raw) as SpotRoundData;
    if (!parsed?.colleges || !Array.isArray(parsed.colleges) || parsed.colleges.length === 0) {
      const data = seedData();
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      return data;
    }
    return parsed;
  } catch {
    const data = seedData();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return data;
  }
}

export function loadSpotShortlistNames(): string[] {
  if (!canUseLocalStorage()) {
    return [];
  }

  const raw = window.localStorage.getItem(SHORTLIST_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((item): item is string => typeof item === "string");
  } catch {
    return [];
  }
}

export function saveSpotShortlistNames(names: string[]): void {
  if (!canUseLocalStorage()) {
    return;
  }
  window.localStorage.setItem(SHORTLIST_KEY, JSON.stringify(names));
}

export function getCollege(data: SpotRoundData, id: string): SpotCollege | undefined {
  return data.colleges.find((college) => college.id === id);
}

export function formatAbs(timestamp: number) {
  return new Date(timestamp).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatElapsed(timestamp: number | undefined, referenceNow = Date.now()): string {
  if (!timestamp) {
    return "—";
  }

  let diff = Math.max(0, referenceNow - timestamp);
  const days = Math.floor(diff / 86400000);
  diff -= days * 86400000;
  const hours = Math.floor(diff / 3600000);
  diff -= hours * 3600000;
  const mins = Math.floor(diff / 60000);
  diff -= mins * 60000;
  const secs = Math.floor(diff / 1000);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(days)}d : ${pad(hours)}h : ${pad(mins)}m : ${pad(secs)}s`;
}

/** Column headers from matrix course keys (API `courseName` order). */
export function streamsFromSeatMatrix(matrix: SpotSeatMatrix): string[] {
  for (const section of ["jee", "mhtcet"] as const) {
    const firstCategory = Object.keys(matrix[section])[0];
    if (firstCategory) {
      return Object.keys(matrix[section][firstCategory] ?? {});
    }
  }
  return [];
}

export function seatClass(value: SpotSeatValue | undefined | null): "" | "avail" | "zero" {
  if (value === "" || value === undefined || value === null) {
    return "";
  }
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return "";
  }
  return num > 0 ? "avail" : "zero";
}

/** True when the cell has no API seat value (use table empty placeholder). */
export function isMissingSeatValue(value: SpotSeatValue | undefined | null): boolean {
  return value === "" || value === undefined || value === null;
}

export function openSeats(college: SpotCollege): number {
  const activeRound = (college.rounds || []).find((round) => round.status === "Active");
  if (!activeRound) {
    return 0;
  }

  let sum = 0;
  (["jee", "mhtcet"] as const).forEach((section) => {
    Object.values(activeRound.matrix[section]).forEach((categoryObj) => {
      Object.values(categoryObj).forEach((value) => {
        if (value !== "" && value != null && !Number.isNaN(Number(value))) {
          sum += Number(value);
        }
      });
    });
  });
  return sum;
}

export function formatDateRange(start: string, end: string): string {
  if (!start && !end) {
    return "";
  }

  const options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short" };
  const startLabel = start ? new Date(start).toLocaleDateString("en-IN", options) : "?";
  const endLabel = end ? new Date(end).toLocaleDateString("en-IN", options) : "?";
  return `${startLabel} – ${endLabel}`;
}

export function isCollegeShortlisted(college: SpotCollege, shortlistedNames: string[]): boolean {
  return shortlistedNames.some((name) => name.toLowerCase() === college.name.toLowerCase());
}

export function matchShortlistedColleges(colleges: SpotCollege[], shortlistedNames: string[]): SpotCollege[] {
  if (shortlistedNames.length === 0) {
    return [];
  }

  return colleges.filter((college) =>
    shortlistedNames.some((name) => {
      const a = name.toLowerCase();
      const b = college.name.toLowerCase();
      return a === b || a.includes(b) || b.includes(a);
    }),
  );
}

export function statusPillClass(status: SpotAdmissionRound["status"]): string {
  if (status === "Active") {
    return "sr2-pill-active";
  }
  if (status === "Not Started") {
    return "sr2-pill-notstarted";
  }
  return "sr2-pill-ended";
}
